import { EditableBadge, type BadgeProps } from '@chatsdet/ui';
import {
  useCompleteTask,
  useCancelTask,
  useTriggerTask,
  useClaimTask,
  useReopenTask,
  useReleaseTask,
} from '../hooks/useTasks';
import {
  STATUS_OPTIONS,
  STATUS_VARIANTS,
  STATUS_LABELS,
  VALID_TRANSITIONS,
  TRANSITION_ACTIONS,
} from '../constants';
import type { AgentTaskDetail, AgentTaskStatus } from '../types';

interface StatusEditableBadgeProps {
  task: AgentTaskDetail;
  onSuccess?: () => void;
  size?: BadgeProps['size'];
}

export function StatusEditableBadge({ task, onSuccess, size }: StatusEditableBadgeProps) {
  const completeTask = useCompleteTask();
  const cancelTask = useCancelTask();
  const triggerTask = useTriggerTask();
  const claimTask = useClaimTask();
  const reopenTask = useReopenTask();
  const releaseTask = useReleaseTask();

  const validTargets = VALID_TRANSITIONS[task.status] ?? [];
  const options = STATUS_OPTIONS.filter(
    (opt) => opt.id === task.status || validTargets.includes(opt.id)
  );

  const isLoading =
    completeTask.isPending ||
    cancelTask.isPending ||
    triggerTask.isPending ||
    claimTask.isPending ||
    reopenTask.isPending ||
    releaseTask.isPending;

  const handleChange = (newStatus: AgentTaskStatus) => {
    if (newStatus === task.status) return;

    const key = `${task.status}->${newStatus}`;
    const action = TRANSITION_ACTIONS[key];
    if (!action) return;

    const mutationOpts = { onSuccess };

    switch (action) {
      case 'trigger':
        triggerTask.mutate(task.id, mutationOpts);
        break;
      case 'claim':
        claimTask.mutate(task.id, mutationOpts);
        break;
      case 'complete':
        completeTask.mutate({ id: task.id }, mutationOpts);
        break;
      case 'cancel':
        cancelTask.mutate(task.id, mutationOpts);
        break;
      case 'reopen':
        reopenTask.mutate(
          { id: task.id, status: newStatus as 'actionable' | 'in_progress' },
          mutationOpts
        );
        break;
      case 'release':
        releaseTask.mutate(
          { id: task.id, status: newStatus as 'actionable' | 'waiting' },
          mutationOpts
        );
        break;
    }
  };

  return (
    <EditableBadge
      value={task.status}
      options={options}
      onChange={handleChange}
      getVariant={(value) => STATUS_VARIANTS[value]}
      formatLabel={(value) => STATUS_LABELS[value]}
      size={size}
      isLoading={isLoading}
      isDisabled={validTargets.length === 0}
      aria-label={`Change status for task: ${task.title}`}
    />
  );
}
